import { BaseApiGatewayService } from "./baseApiGatewayService";
import { HttpMethod, AdditionalParams, ApiGatewayClientResponse } from "../types/api";
import { nativeBridge } from '../components/auth/nativeBridge';
import errorManager from './errorManager';
import { detectEnvironment } from "../utils";
import { Env } from "./serviceConfig";


export class AppApiGatewayService extends BaseApiGatewayService {
    private originalInvokeApi: any;
    private authToken: string | null = null;

    constructor(
        invokeUrl: string,
        region: string,
        authToken?: string
    ) {
        super(invokeUrl, region);
        if (authToken) {
            this.authToken = authToken;
        }

        // Store original invokeApi method before overriding
        this.originalInvokeApi = this.apiGatewayClient.invokeApi.bind(this.apiGatewayClient);

        // Override with retry logic
        this.apiGatewayClient.invokeApi = this.invokeApiWithRetry.bind(this);


        if (process.env.DEBUG_AUTH_USER_ID) {
            console.log(`detect DEBUG_AUTH_USER_ID env var. override authornization to user id ${process.env.DEBUG_AUTH_USER_ID}`)
        }

    }

    static async create(
        invokeUrl: string,
        region: string
    ): Promise<AppApiGatewayService> {
        let authToken = '';
        if (nativeBridge.isAvailable()) {
            const nativeCredentials = await nativeBridge.getCredentials();
            authToken = nativeCredentials.accessToken;
        }

        return new AppApiGatewayService(invokeUrl, region, authToken);
    }

    protected getPathPrefix(): string {
        return detectEnvironment() == Env.LOCAL ? "/api" : super.getPathPrefix() + '';
    }

    protected getAuthToken(): string | null {
        return this.authToken;
    }

    protected getRequestParams(): AdditionalParams {
        const headers = super.getRequestParams().headers;
        const token = this.getAuthToken();

        // Only add Authorization header if token exists
        if (token) {
            headers!['Authorization'] = `Bearer ${token}`;
        }

        return { headers };
    }

    /**
     * generic api call
     */
    public async invokeApi(path: string, opts: { method?: HttpMethod, queryParams?: Record<string, string | number | boolean>, headers?: Record<string, unknown> } = {}): Promise<ApiGatewayClientResponse> {
        const {
            method = "GET" as HttpMethod,
            queryParams = {},
            headers = {},
        } = opts;

        const additionalParams = this.getRequestParams();
        additionalParams.headers = { ...additionalParams.headers, ...headers };
        additionalParams.queryParams = queryParams;

        path = this.getPathPrefix() + path;
        const response = await this.apiGatewayClient.invokeApi(
            {},
            path,
            method,
            additionalParams,
        ) as ApiGatewayClientResponse;
        return response
    }

    /**
     * Fetch event metadata (thumbnail, video URL, timing) from EVM.
     */
    public async fetchEVMEvent(eventId: string): Promise<ApiGatewayClientResponse> {
        const additionalParams = this.getRequestParams();
        additionalParams.headers = { ...additionalParams.headers, Accept: 'application/json' };

        return await this.apiGatewayClient.invokeApi(
            {},
            `/capigw/evm/v2/history/events/${eventId}?supported_thumbnail_format=JPEG&thumbnail_resolution=1024`,
            'GET',
            additionalParams,
        ) as ApiGatewayClientResponse;
    }

    /**
     * Request a transcoded video download URL from Phoenix via the backend proxy.
     */
    public async fetchVideoDownloadUrl(body: Record<string, any>): Promise<ApiGatewayClientResponse> {
        const additionalParams = this.getRequestParams();
        additionalParams.headers = {
            ...additionalParams.headers,
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };

        return await this.apiGatewayClient.invokeApi(
            {},
            '/capigw/ai-playground/v1/video',
            'POST',
            additionalParams,
            body,
        ) as ApiGatewayClientResponse;
    }


    protected async handleAuthError(error: any): Promise<boolean> {
        // Fallback auth refresh
        try {
            const credentials = await nativeBridge.refreshCredentials();
            this.authToken = credentials.accessToken;
            return true;
        } catch (refreshError) {
            console.error('Failed to refresh credentials:', refreshError);
            errorManager.showError('Authentication failed. Please refresh the page.');
            return false;
        }
    }

    private async invokeApiWithRetry(
        params: Record<string, unknown>,
        pathTemplate: string,
        method: HttpMethod,
        additionalParams?: any,
        body?: Record<string, any>
    ): Promise<ApiGatewayClientResponse> {
        try {
            if (process.env.DEBUG_AUTH_USER_ID && detectEnvironment() === Env.LOCAL) {
                if (!additionalParams) additionalParams = {}
                if (!additionalParams.headers) additionalParams.headers = {}
                additionalParams.headers['x-auth-userid'] = process.env.DEBUG_AUTH_USER_ID
            }

            const response = await this.originalInvokeApi(params, pathTemplate, method, additionalParams, body);
            console.log("Received response from API Gateway:", response);
            return response;
        } catch (error: any) {
            console.error("Error in App API Gateway request:", error);

            if (error.message === 'Network Error' || error?.response?.status === 401 || error?.status === 401) {
                console.log("Auth error detected, attempting to refresh token");
                const retried = await this.handleAuthError(error);
                if (retried) {
                    const newParams = this.getRequestParams();
                    const updatedAdditionalParams = additionalParams?.queryParams ?
                        { ...newParams, queryParams: additionalParams.queryParams } : newParams;

                    return await this.originalInvokeApi(params, pathTemplate, method, updatedAdditionalParams, body);
                }
            }
            throw error;
        }
    }
}